'use client';

import React, { useState, useEffect } from 'react';
import {
  Box,
  BoxProps,
  Container,
  Divider,
  Group,
  Text,
  Stack,
  useMantineTheme
} from '@mantine/core';
import LogoImage from '../../public/img/logo.svg';
import FAIcon, { ValidIconName } from './FAIcon';
import OpacityLink from './OpacityLink';
import { useResponsive } from '@/hooks/use-responsive';
import { useAuthContext } from '@/context/Auth';
import { useCurrentUser } from '@/hooks/use-current-user';
import styled from '@emotion/styled';
import { getCachedMenuData, setCachedMenuData, CACHE_KEYS } from '@/utils/menuCache';

/**
 * Menu Item Type
 */
interface MenuItem {
  id: string;
  menuGroup: string;
  parentId: string | null;
  type: string;
  title: string;
  pageId: string | null;
  postId: string | null;
  slug: string;
  url: string;
  routeName: string | null;
  routeParams: string | null;
  icon: string | null;
  color: string | null;
  target: string;
  isActive: boolean;
  isPublished: boolean;
  meta: any;
  created_at: string;
  updated_at: string;
  order: number;
  children?: MenuItem[];
}

interface SiteFooterProps extends BoxProps {
  [prop: string]: any;
}

interface SocialLink {
  icon: ValidIconName;
  href: string;
}

/**
 * Styled components for footer menu
 */
const FooterMenuWrapper = styled.div`
  display: flex;
  gap: 30px;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`;

const FooterMenuGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media screen and (max-width: 768px) {
    align-items: center;
  }
`;

const FooterMenuButton = styled.button<{ isParent?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: white;
  font-size: 14px;
  font-weight: ${({ isParent }) => (isParent ? 600 : 500)};
  opacity: ${({ isParent }) => (isParent ? 1 : 0.8)};
  transition: opacity 200ms ease;

  &:hover {
    opacity: 1;
  }
`;

const FooterChildItem = styled.button`
  background: none;
  border: none;
  padding: 0;
  padding-left: 12px;
  cursor: pointer;
  color: white;
  font-size: 13px;
  font-weight: 400;
  opacity: 0.7;
  transition: opacity 200ms ease;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 4px;
    background-color: var(--color-primary, #007bff);
    border-radius: 50%;
  }

  &:hover {
    opacity: 1;
  }

  @media screen and (max-width: 768px) {
    padding-left: 16px;
  }
`;

const SiteFooter: React.FC<SiteFooterProps> = ({ className, ...props }) => {
  const { responsive } = useResponsive();
  const theme = useMantineTheme();
  const { state: { twoFactorAuthenticated }, dispatch } = useAuthContext();
  const [currentUser, authStatus] = useCurrentUser();
  const [footerMenuList, setFooterMenuList] = useState<Array<MenuItem>>([]);

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch({ type: 'OPEN_MODAL' });
  };

  // Fetch footer menu items
  useEffect(() => {
    const fetchFooterMenu = async () => {
      try {
        // Check localStorage cache first
        const cachedData = getCachedMenuData(CACHE_KEYS.FOOTER_MENU);

        if (cachedData && cachedData.success && cachedData.data) {
          // Use cached data - NO API CALL!
          const menus = Array.isArray(cachedData.data) ? cachedData.data : [];
          setFooterMenuList(menus);
          return;
        }

        // No cache found, fetch from API
        const response = await fetch('/api/menus/published?menuGroup=footer');
        const data = await response.json();

        if (data.success) {
          // Save to localStorage cache
          setCachedMenuData(CACHE_KEYS.FOOTER_MENU, data);

          const menus = Array.isArray(data.data) ? data.data : [];
          setFooterMenuList(menus);
        }
      } catch (error) {
        console.error("Error fetching footer menu:", error);
        setFooterMenuList([]);
      }
    };

    void fetchFooterMenu();
  }, []);

  /**
   * Build nested menu structure from flat array
   */
  const buildNestedMenu = (items: MenuItem[]): MenuItem[] => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return [];
    }

    const itemMap = new Map<string, MenuItem>();
    const rootItems: MenuItem[] = [];

    items.forEach(item => {
      if (item && item.id) {
        itemMap.set(item.id, { ...item, children: [] });
      }
    });

    items.forEach(item => {
      if (!item || !item.id) return;

      const menuItem = itemMap.get(item.id)!;
      if (item.parentId && itemMap.has(item.parentId)) {
        const parent = itemMap.get(item.parentId)!;
        parent.children = parent.children || [];
        parent.children.push(menuItem);
      } else {
        rootItems.push(menuItem);
      }
    });
//Sort root items and their children by order
    const sortByOrder = (a: MenuItem, b: MenuItem) => (a.order ?? 0) - (b.order ?? 0);
    rootItems.sort(sortByOrder);
    rootItems.forEach(item => {
      if (item.children && item.children.length > 0) {
        item.children.sort(sortByOrder);
      }
    });

    return rootItems;
  };

  const nestedMenuList = buildNestedMenu(footerMenuList);

  /**
   * Handle menu item click - YOU HAVE FULL CONTROL HERE
   * @param menuItem - The clicked menu item
   */
  const handleMenuItemClick = (menuItem: MenuItem) => {
    if (menuItem.url) {
      if (menuItem.target === '_blank') {
        window.open(menuItem.url, '_blank');
      } else {
        window.open(menuItem.url, '_self');
      }
    }
  };

  const renderMenuItem = (menuItem: MenuItem) => {
    const hasChildren = menuItem.children && menuItem.children.length > 0;

    if (hasChildren) {
      return (
        <FooterMenuGroup key={menuItem.id}>
          <FooterMenuButton
            isParent={true}
            onClick={() => handleMenuItemClick(menuItem)}
            aria-label={menuItem.title}
          >
            {menuItem.title}
          </FooterMenuButton>
          {menuItem.children!.map((child) => (
            <FooterChildItem
              key={child.id}
              onClick={() => handleMenuItemClick(child)}
              aria-label={child.title}
            >
              {child.title}
            </FooterChildItem>
          ))}
        </FooterMenuGroup>
      );
    }

    return (
      <FooterMenuButton
        key={menuItem.id}
        onClick={() => handleMenuItemClick(menuItem)}
        aria-label={menuItem.title}
      >
        {menuItem.title}
      </FooterMenuButton>
    );
  };

  const socialLinks: SocialLink[] = [
    {
      icon: 'linkedin-in',
      href: 'https://www.linkedin.com/company/99208016/admin/feed/posts/'
    },
    {
      icon: 'facebook',
      href: 'https://www.facebook.com/HomeHealthSolutions.io'
    },
    {
      icon: 'instagram',
      href: 'https://www.instagram.com/homehealthsolutions.io/'
    },
    {
      icon: 'twitter',
      href: 'https://twitter.com/homehealthsolutions.io/'
    },
  ];

  return (
    <Box
      component="footer"
      py={100}
      style={{
        backgroundColor: theme.colors.dark[7]
      }}
      className={className}
      {...props}
    >
      <Container size="xl">
        <Group
          wrap="nowrap"
          justify="space-between"
          gap={responsive({
            xs: 30,
            md: 0,
          })}
          style={{
            flexDirection: responsive({
              xs: 'column',
              md: 'row',
            }),
          }}
        >
          {/* Logo */}
          <Box c="white" w={200}>
            <LogoImage />
          </Box>

          {/* Navigation Links */}
          <Group
            gap={30}
            style={{
              flexDirection: responsive({
                xs: 'column',
                sm: 'row',
              }),
            }}
          >
            <FooterMenuWrapper>
              {nestedMenuList.map((menuItem) => renderMenuItem(menuItem))}
            </FooterMenuWrapper>

            {/* Conditional Login/Account Link */}
            {currentUser && twoFactorAuthenticated ? (
              <OpacityLink href="/profile" ariaLabel="Profile">
                <Text c="white" fz={14} fw={500}>
                  Account
                </Text>
              </OpacityLink>
            ) : (
              <Text
                c="white"
                fz={14}
                fw={500}
                onClick={handleLoginClick}
                style={{ cursor: 'pointer' }}
              >
                Login
              </Text>
            )}
          </Group>

          {/* Social Links */}
          <Group
            gap={20}
            w={responsive({
              xs: 'auto',
              lg: 200,
            })}
            justify={responsive({
              xs: 'center',
              md: 'flex-end',
            })}
          >
            {socialLinks.map((link) => (
              <OpacityLink
                key={link.icon}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                transition={false}
                ariaLabel={link.href}
              >
                <FAIcon
                  name={link.icon}
                  prefix="fab"
                  size={16}
                  style={{
                    color: 'white'
                  }}
                />
              </OpacityLink>
            ))}
          </Group>
        </Group>

        <Divider my={50} opacity={0.1} />

        {/* Bottom Section */}
        <Group
          gap={responsive({
            xs: 20,
            md: 50,
          })}
          justify="center"
          style={{
            flexDirection: responsive({
              xs: 'column',
              md: 'row',
            }),
          }}
        >
          <Text fz={14} c="dark.1">
            © {new Date().getFullYear()} HomeHealthSolutions. All rights reserved.
          </Text>

          <OpacityLink
            href="/privacy"
            ariaLabel="Privacy Policy"
          >
            <Text fz={14} c="white">
              Privacy Policy
            </Text>
          </OpacityLink>

          <OpacityLink href="/terms" ariaLabel="Terms of Service">
            <Text fz={14} c="white">
              Terms of Service
            </Text>
          </OpacityLink>
        </Group>
      </Container>
    </Box>
  );
};

export default SiteFooter;